import { useCallback, useEffect, useState } from 'react';
import {
  EyeFilled,
  RedoOutlined,
  UploadOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../components';
import { Card, Row, Col, Statistic, Space, Tag, Badge } from 'antd';
import { IInventoryOutput, IProduct } from '../../shared/interfaces';
import api from '../../services/api';
import { useToast } from '../../hooks/toast';
import InventoryOutputCreate from './Create';
import { formatDate } from '../../shared/utils/format-date';
import InventoryOutputView from './View';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const InventoryOutputs: React.FC = () => {
  const { addToast } = useToast();
  const { permissionFind } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [outputs, setOutputs] = useState<IInventoryOutput[]>([]);
  const [output, setOutput] = useState<IInventoryOutput | null>(null);
  const [products, setProducts] = useState<IProduct[]>([]);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [modalView, setModalView] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const listInventoryEntries = useCallback(async () => {
    setLoading(true);
    setOutputs([]);
    try {
      const response = await api.post('/inventory-outputs/list', {
        take: limit,
        skip: (page - 1) * limit,
        keyword: search,
      });
      setPages(response.data.count);
      setOutputs(response.data.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar as saídas',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const listProducts = useCallback(async () => {
    await api
      .get(`/products/select`)
      .then((response) => {
        setProducts(response.data);
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: 'Erro ao listar os produtos',
        });
      });
  }, [addToast]);

  useEffect(() => {
    listInventoryEntries();
  }, [page, search, limit, listInventoryEntries]);

  const renderModalInventoryOugputCreate = () => {
    if (modalCreate)
      return (
        <InventoryOutputCreate
          open={modalCreate}
          close={() => setModalCreate(false)}
          setOutputs={setOutputs}
          products={products}
        />
      );
  };

  const renderModalInventoryView = () => {
    if (modalView)
      return (
        <InventoryOutputView
          open={modalView}
          close={() => setModalView(false)}
          output={output}
        />
      );
  };

  useEffect(() => {
    listProducts();
  }, [listProducts]);

  const getTypeColor = (type: string | undefined) => {
    if (type === 'treatment') return 'blue';
    if (type === 'dental_treatment') return 'green';
    return 'default';
  };

  const getTypeLabel = (type: string | undefined) => {
    if (type === 'treatment') return 'Tratamento';
    if (type === 'dental_treatment') return 'Odontológico';
    return 'Desconhecido';
  };

  return (
    <PageLayout>
      <PageIntro
        badge="Estoque"
        icon={<UploadOutlined />}
        title="Retiradas do estoque"
        description="Use esta tela para acompanhar ou registrar produtos que saíram do estoque manualmente. Saídas por venda e tratamento podem ser automáticas."
      />
      <MenuOptions
        showButtonBack
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => listInventoryEntries(),
          },
          permissionFind([PermissionActions.inventoriesOutputCreate])
            ? {
                label: 'Registrar Retirada',
                key: '2',
                icon: <PlusOutlined />,
                onClick: () => {
                  setOutput(null);
                  setModalCreate(true);
                },
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        {outputs.length > 0 && (
          <Card style={{ marginBottom: 20 }}>
            <Row gutter={16}>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title="Retiradas registradas"
                  value={outputs.length}
                  prefix={<UploadOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title="Quantidade retirada"
                  value={outputs.reduce((sum, o) => sum + (o.quantity || 0), 0)}
                  prefix={<UploadOutlined style={{ marginRight: 4 }} />}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title="Ligadas a tratamento"
                  value={outputs.filter((o) => o.type === 'treatment').length}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title="Ligadas a odontologia"
                  value={
                    outputs.filter((o) => o.type === 'dental_treatment').length
                  }
                />
              </Col>
            </Row>
          </Card>
        )}

        <ATable
          scroll={{ y: 510, x: 1000 }}
          columns={[
            {
              title: 'Data',
              dataIndex: 'date',
              width: 120,
              render: (value: string | null) => (
                <>{value ? formatDate(value) : ''}</>
              ),
            },
            {
              title: 'Produto',
              dataIndex: 'product_name',
              width: 200,
            },
            {
              title: 'Origem da retirada',
              dataIndex: 'treatment_code',
              width: 120,
              render: (value: number | null, record: IInventoryOutput) => (
                <>
                  {value ? (
                    <Space>
                      <Badge
                        count={value}
                        style={{
                          backgroundColor: '#1890ff',
                          fontSize: 12,
                          height: 24,
                          lineHeight: '24px',
                          minWidth: 24,
                        }}
                      />
                      <Tag color={getTypeColor(record.type)}>
                        {getTypeLabel(record.type)}
                      </Tag>
                    </Space>
                  ) : (
                    <span style={{ color: '#999' }}>-</span>
                  )}
                </>
              ),
            },
            {
              title: 'Qtd. retirada',
              dataIndex: 'quantity',
              width: 100,
              align: 'center' as const,
            },
            {
              title: 'Motivo',
              dataIndex: 'observation',
              width: 150,
              render: (value: string | null) => (
                <>
                  {value ? (
                    <span title={value}>{value.substring(0, 20)}...</span>
                  ) : (
                    '-'
                  )}
                </>
              ),
            },
            {
              title: 'Opções',
              width: 80,
              fixed: 'right' as const,
              render: (cell: IInventoryOutput) => (
                <DropdownOptions
                  items={[
                    {
                      label: 'Visualizar',
                      key: '1',
                      icon: <EyeFilled />,
                      onClick: () => {
                        setOutput(cell);
                        setModalView(true);
                      },
                    },
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={outputs}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {renderModalInventoryOugputCreate()}
      {renderModalInventoryView()}
    </PageLayout>
  );
};

export default InventoryOutputs;
